import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function Header() 
{
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const noTopMenuRoutes = ["/", "/login", "/about-us", "/contact-us", "/employee/new"];
  const LEGACY_PORTAL_BASE = (process.env.REACT_APP_LEGACY_PORTAL_BASE || "http://127.0.0.1").replace(/\/$/, "");
  const localUser = (() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const showEmployeeTopMenu = Boolean(localUser) && !noTopMenuRoutes.includes(location.pathname);
  const isLoggedIn = Boolean(localUser);
  const isPublicHome = location.pathname === "/";
  const navClassName = isPublicHome
    ? "navbar navbar-light bg-white custom-header-home fixed-top"
    : "navbar navbar-dark custom-header-bg fixed-top";
  const headerTextClass = isPublicHome ? "text-dark" : "text-white";
  const currentModuleId = parseInt(localUser?.moduleId || user?.moduleId, 10);
  const currentDeptId = parseInt(localUser?.deptid || user?.deptid, 10);
  const currentEmpId = parseInt(localUser?.id || user?.id, 10);
  const isGuesthouseAdmin = currentModuleId === 10;

  // Guest booking-notification (email/SMS) on/off switch — Module 10 only, browser-local.
  // Default ON when nothing is stored yet.
  const [ghNotifyEnabled, setGhNotifyEnabled] = useState(() => {
    try {
      return localStorage.getItem("gh_notify_enabled") !== "0";
    } catch {
      return true;
    }
  });
  const toggleGhNotify = () => {
    setGhNotifyEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("gh_notify_enabled", next ? "1" : "0");
      } catch {
        // localStorage unavailable — toggle still works for this page view
      }
      return next;
    });
  };
  const isGuesthouseReception = [11, 22, 23, 27, 28].includes(currentModuleId);
  const isGuesthouseFinance = currentModuleId === 25;
  const isDeputyRegistrarColleges = currentModuleId === 26;
  // Keep in sync with ALLOWED_PROFILE_MODULES in Profile.jsx — only these modules
  // have working content on /profile. Every other module used to fall through to
  // /profile anyway and hit "Unauthorized access Or Session Timed out.", since
  // Profile.jsx rejects anything outside this list. Dashboard.jsx already handles
  // every moduleId correctly (redirects on where needed, renders real content,
  // or a safe generic fallback), so anything not in this list should go there.
  const PROFILE_HOME_MODULES = [1, 3, 7, 13, 15, 16, 17, 19, 21, 29];
  const homeTarget = !localUser
    ? "/"
    : isGuesthouseAdmin
      ? "/guesthouse/pending-bookings"
      : isGuesthouseReception
        ? "/guesthouse/room-logbook"
        : isGuesthouseFinance
          ? "/payment-details"
          : isDeputyRegistrarColleges
          ? "/dr-colleges"
          : currentModuleId === 5
            ? "/dashboard"
            : currentModuleId === 9
              ? "/resolver-complaint-list"
              : PROFILE_HOME_MODULES.includes(currentModuleId)
                ? "/profile"
                : "/dashboard";
  const handleLogout = () => {
    const from = `${location.pathname}${location.search}${location.hash}`;
    navigate("/logout", { state: { from } });
  };

  const CHANGE_PASS_MODULES = [1, 3, 7, 13, 14, 15, 16, 19, 29];
  const CONTRACTUAL_ATTENDANCE_DEPTS = [26, 75, 99, 109, 110, 192, 210, 211, 214];
  // PFNo=8750 (EmployeeID=53, Sudhir Goyal) — read-only access to the full Employee List
  // (same view ModuleID=10 gets, no Edit option), granted individually per request.
  const EMPLOYEE_LIST_VIEWER_EMP_IDS = [53];
  const ADVANCED_ROLE_MODULES = [1, 3, 7, 13, 16, 29];
  const switchRoleHref = ADVANCED_ROLE_MODULES.includes(currentModuleId) ? "/switch-role" : "/basic-role";

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const commonMenuItems = [
    { label: "Departments/Centres", icon: "bi-diagram-3", href: "/infra/departments-centres", internal: true },
    { label: "Directory", icon: "bi-card-list", href: "/directory", internal: true },
    { label: "Holiday List", icon: "bi-calendar-event", href: "/holidays", internal: true, hiddenModules: [2] },
    { label: "Change Password", icon: "bi-key", href: "/change-password", internal: true, allowedModules: CHANGE_PASS_MODULES },
    { label: "Switch Roles", icon: "bi-arrow-left-right", href: switchRoleHref, internal: true },
    { label: "Logout", icon: "bi-box-arrow-right", action: "logout" }
  ];

  const filteredCommonMenuItems = commonMenuItems.filter(
    item =>
      (!item.allowedModules || item.allowedModules.includes(currentModuleId)) &&
      (!item.hiddenModules || !item.hiddenModules.includes(currentModuleId))
  );

  const isMasterModule = currentModuleId === 4;
  const moduleMenuItems = isGuesthouseAdmin ? [
    { label: "PendingBookings", icon: "bi-clock-history", href: "/guesthouse/pending-bookings", internal: true, color: "btn-danger" },
    { label: "RoomLog",         icon: "bi-journal-text",  href: "/guesthouse/room-logbook",     internal: true, color: "btn-info" },
    { label: "CheckIn-Out", icon: "bi-journal-text",  href: "/guesthouse/checkin-checkout", internal: true, color: "btn-secondary" },
    { label: "Tariff", icon: "bi-cash-stack", href: "/guesthouse/tariff", internal: true, color: "btn-dark" },
    { label: "BookingRequest", icon: "bi-journal-plus", href: "/guesthouse/booking-request-admin", internal: true, color: "btn-warning" },
    { label: "Rooms", icon: "bi-door-open", href: "/guesthouse/rooms", internal: true, color: "btn-success" },
    { label: "Departments", icon: "bi-people-fill", href: "/members-list", internal: true, color: "btn-indigo" },
    { label: "Fellows", icon: "bi-person-vcard", href: "/fellows-list", internal: true, color: "btn-coral" },
    { label: "CollegeTeachers", icon: "bi-mortarboard", href: "/college-teachers-list", internal: true, color: "btn-teal" },
    { label: "EmpList",     icon: "bi-people",       href: "/employee-list",                    internal: true, color: "btn-secondary" },
    { label: "PendingEmp",  icon: "bi-patch-check",  href: "/pending-verifications",            internal: true, color: "btn-danger" },
    { label: "PendingTeach", icon: "bi-patch-check", href: "/pending-verifications-teachers",   internal: true, color: "btn-warning" },
    { label: "PendingMember", icon: "bi-patch-check", href: "/pending-verifications-members",  internal: true, color: "btn-success" },
    { label: "NewFellow",     icon: "bi-person-plus", href: "/guesthouse-new-member",           internal: true, color: "btn-primary" },
    { label: "DepttRoles",    icon: "bi-people-gear", href: "/departmental-roles",               internal: true, color: "btn-indigo" },
    { label: "IndvLedger",   icon: "bi-journal-text", href: "/individual-ledger",                internal: true, color: "btn-teal"   },
    { label: "Invoices",  icon: "bi-receipt",      href: "/guesthouse/invoice-details",       internal: true, color: "btn-warning" },
    { label: "SMSLogs",      icon: "bi-chat-dots",    href: "/sms-logs",                         internal: true, color: "btn-success" }
  ] : isMasterModule ? [
    { label: "DentalAtt", icon: "bi-clipboard-check", href: "/dental-att", internal: true, color: "btn-pink" },
    { label: "AdminAtt", icon: "bi-clipboard-check", href: "/admin-att", internal: true, color: "btn-orange" },
    { label: "CDOEAtt", icon: "bi-clipboard-check", href: "/cdoe-att", internal: true, color: "btn-yellow" },
    { label: "Attendance", icon: "bi-calendar-check", href: "/attendance", internal: true, color: "btn-info" },
    { label: "StudentAtt", icon: "bi-clipboard-check", href: "/student-upload-att", internal: true, color: "btn-cyan" },
    { label: "Display Daily Attendance", icon: "bi-calendar2-week", href: "/display-daily-attendance", internal: true, color: "btn-primary" },
    { label: "LeaveDetails", icon: "bi-person-lines-fill", href: "/leave-details-office", internal: true, color: "btn-purple" },
    { label: "Achievements", icon: "bi-trophy", href: "/achievements-office", internal: true, color: "btn-pink" },
    { label: "PendingEmp", icon: "bi-patch-check", href: "/pending-verifications", internal: true, color: "btn-danger" },
    { label: "EmpList", icon: "bi-people", href: "/employee-list", internal: true, color: "btn-secondary" },
    { label: "SeniorityList", icon: "bi-list-ol", href: "/seniority-list-office", internal: true, color: "btn-dark" },
    { label: "Departments", icon: "bi-people-fill", href: "/members-list", internal: true, color: "btn-teal" },
    { label: "Fellows", icon: "bi-person-vcard", href: "/fellows-list", internal: true, color: "btn-coral" },
    { label: "CollegeTeachers", icon: "bi-mortarboard", href: "/college-teachers-list", internal: true, color: "btn-info" },
    { label: "ShiftEmp", icon: "bi-arrow-repeat", href: "/shift-employee", internal: true, color: "btn-lime" },
    { label: "SpecialRoles", icon: "bi-person-gear", href: "/special-roles", internal: true, color: "btn-maroon" },
    { label: "DepttRoles",        icon: "bi-people-gear", href: "/departmental-roles", internal: true, color: "btn-indigo" },
    // Removed Student Registration (Instructions) and Temporarily Closed buttons
    { label: "StuRegi", icon: "bi-person-plus", href: "/student-registration", internal: true, color: "btn-success" },
    { label: "NewItem", icon: "bi-plus-square", href: "/new-item", internal: true, color: "btn-pink" },
    { label: "SMSLogs", icon: "bi-chat-dots", href: "/sms-logs", internal: true, color: "btn-success" }
  ] : (currentModuleId === 1 || currentModuleId === 29) ? [
    { label: "Profile",              icon: "bi-person-badge",     href: "/profile",                                          internal: true,  color: "btn-info" },
    ...(CONTRACTUAL_ATTENDANCE_DEPTS.includes(currentDeptId)
      ? [
          { label: "Mark Attendance", icon: "bi-fingerprint",      href: "/mark-attendance",                                                              internal: true,  color: "btn-warning" },
          { label: "View Attendance", icon: "bi-calendar2-check",  href: "/view-attendance",                                                              internal: true,  color: "btn-orange"  }
        ]
      : []),
    { label: "LeaveDetails",        icon: "bi-calendar-check",   href: "/LeaveDetails", internal: true, color: "btn-warning" },
    { label: "Achievements",        icon: "bi-trophy",           href: "/MyAchievements", internal: true, color: "btn-pink" },
    ...(EMPLOYEE_LIST_VIEWER_EMP_IDS.includes(currentEmpId)
      ? [{ label: "EmpList", icon: "bi-people", href: "/employee-list", internal: true, color: "btn-secondary" }]
      : []),
    { label: "ResiComplaints", icon: "bi-house-exclamation", href: "/residential-complaints",                           internal: true,  color: "btn-primary" },
    { label: "OfficialComplaints",  icon: "bi-building-exclamation", href: "/official-complaints", internal: true, color: "btn-danger" },
    { label: "GuestHouse",          icon: "bi-building",          href: "/guesthouse/booking-status",                            internal: true,  color: "btn-secondary" },
    { label: "SeniorityList",       icon: "bi-list-ol",           href: "/seniority-list",                                         internal: true,  color: "btn-dark" }
  ] : currentModuleId === 7 ? [
    { label: "Profile", icon: "bi-person-badge", href: "/profile", internal: true, color: "btn-info" },
    ...(CONTRACTUAL_ATTENDANCE_DEPTS.includes(currentDeptId)
      ? [
          { label: "Mark Attendance", icon: "bi-fingerprint",     href: "/mark-attendance", internal: true, color: "btn-warning" },
          { label: "View Attendance", icon: "bi-calendar2-check", href: "/view-attendance", internal: true, color: "btn-orange"  }
        ]
      : []),
    { label: "Leave Details", icon: "bi-calendar-check", href: "/LeaveDetails", internal: true, color: "btn-secondary" },
    { label: "Achievements", icon: "bi-trophy", href: "/MyAchievements", internal: true, color: "btn-pink" },
    { label: "ResiComplaints", icon: "bi-house-exclamation", href: "/residential-complaints", internal: true, color: "btn-primary" },
    { label: "Official Complaints", icon: "bi-building-exclamation", href: "/official-complaints", internal: true, color: "btn-danger" },
    { label: "Seniority List",    icon: "bi-list-ol",          href: "/seniority-list",   internal: true, color: "btn-dark" }
  ] : isGuesthouseReception ? [
    { label: "RoomLog",     icon: "bi-journal-text", href: "/guesthouse/room-logbook",     internal: true, color: "btn-info" },
    { label: "CheckIn-Out", icon: "bi-journal-text", href: "/guesthouse/checkin-checkout", internal: true, color: "btn-secondary" }
  ] : isGuesthouseFinance ? [
    { label: "RoomLog",       icon: "bi-journal-text",       href: "/guesthouse/room-logbook",     internal: true, color: "btn-info" },
    { label: "CheckIn-Out",   icon: "bi-door-open",          href: "/guesthouse/checkin-checkout", internal: true, color: "btn-secondary" },
    { label: "IndvLedger",   icon: "bi-person-lines-fill",  href: "/individual-ledger",           internal: true, color: "btn-teal" },
    { label: "Invoices",  icon: "bi-receipt",            href: "/guesthouse/invoice-details",  internal: true, color: "btn-warning" },
    { label: "Payment Dtls",  icon: "bi-cash-stack",         href: "/payment-details",             internal: true, color: "btn-primary" }
  ] : isDeputyRegistrarColleges ? [
    { label: "CollegeList",        icon: "bi-building",      href: "/dr-colleges/college-list",       internal: true, color: "btn-primary" },
    { label: "PendingPrincipals",  icon: "bi-patch-check",   href: "/dr-colleges/pending-principals", internal: true, color: "btn-warning" },
    { label: "CollegeTeachers",    icon: "bi-mortarboard",   href: "/college-teachers-list",          internal: true, color: "btn-teal" }
  ] : [
    ...([2, 5, 9, 15].includes(currentModuleId)
      ? []
      : [{ label: "Profile", icon: "bi-person-badge", href: "/profile", internal: true, color: "btn-info" }]),
    ...( [3, 13, 15, 16, 19].includes(currentModuleId)
      ? [{ label: "ResiComplaints", icon: "bi-house-exclamation", href: "/residential-complaints", internal: true, color: "btn-primary" }]
      : [] ),
    ...( [3, 5, 13, 15, 16].includes(currentModuleId)
      ? [{ label: "OfficialComplaints", icon: "bi-building-exclamation", href: "/official-complaints", internal: true, color: "btn-danger" }]
      : [] ),
    ...( [3, 13].includes(currentModuleId)
      ? [{ label: "Seniority List", icon: "bi-list-ol", href: "/seniority-list", internal: true, color: "btn-dark" }]
      : [] ),
    ...( [3, 13, 16].includes(currentModuleId)
      ? [{ label: "Achievements", icon: "bi-trophy", href: "/MyAchievements", internal: true, color: "btn-pink" }]
      : [] ),
    ...( currentModuleId === 16 && CONTRACTUAL_ATTENDANCE_DEPTS.includes(currentDeptId)
      ? [
          { label: "Mark Attendance", icon: "bi-fingerprint",     href: "/mark-attendance",                                                             internal: true,  color: "btn-warning" },
          { label: "View Attendance", icon: "bi-calendar2-check", href: "/view-attendance",                                                             internal: true,  color: "btn-orange"  }
        ]
      : [] ),
    ...( currentModuleId === 5
      ? [
          { label: "Attendance",    icon: "bi-calendar-check", href: "/student-attendance",   internal: true, color: "btn-primary" },
          { label: "Students List", icon: "bi-people",          href: "/students-list",        internal: true, color: "btn-success" },
          { label: "Leave",         icon: "bi-calendar-x",     href: "/student-leave",         internal: true, color: "btn-warning" },
          { label: "Verified List", icon: "bi-patch-check",    href: "/student-verified-list", internal: true, color: "btn-info"    }
        ]
      : [] ),
    ...( currentModuleId === 9
      ? [
          { label: "Complaint List",     icon: "bi-list-ul",        href: "/resolver-complaint-list", internal: true,  color: "btn-primary" },
          { label: "Resolve Complaints", icon: "bi-wrench",          href: "/resolver-action",         internal: true,  color: "btn-success" },
          { label: "Pending Complaints", icon: "bi-hourglass-split", href: "/resolver-pending",        internal: true,  color: "btn-warning" },
          ...([95, 102, 221].includes(currentDeptId)
            ? [
                { label: "Employee List",         icon: "bi-people",            href: "/employee-list",                                                     internal: true,  color: "btn-secondary" },
                { label: "Pending Verifications", icon: "bi-patch-check",       href: "/pending-verifications",                                             internal: true,  color: "btn-danger"    },
                { label: "Pending Leave",         icon: "bi-calendar-x",        href: `${LEGACY_PORTAL_BASE}/LeaveMgmt/FormPendingLeaves.php`,              internal: false, color: "btn-orange"    },
                { label: "Leave Details",         icon: "bi-calendar-check",    href: "/leave-details-office",                                              internal: true,  color: "btn-info"      },
                { label: "Pending NOC",           icon: "bi-file-earmark-check",href: `${LEGACY_PORTAL_BASE}/NOC/FormPendingNOC.php`,                       internal: false, color: "btn-purple"    },
                { label: "Shift Employee",        icon: "bi-arrow-repeat",      href: "/shift-employee",                                                    internal: true,  color: "btn-indigo"    },
                { label: "Seniority List",        icon: "bi-list-ol",           href: "/seniority-list",                                                    internal: true,  color: "btn-dark"      }
              ]
            : []),
          ...(currentDeptId === 97
            ? [
                { label: "CheckIn-CheckOut", icon: "bi-door-open", href: "/guesthouse/checkin-checkout", internal: true, color: "btn-teal" }
              ]
            : []),
          { label: "Print Complaints", icon: "bi-printer", href: "/print-complaints", internal: true, color: "btn-info" }
        ]
      : [] ),
    ...( [2, 4, 15].includes(currentModuleId)
      ? [{ label: "Leave Details", icon: "bi-calendar-check", href: "/leave-details-office", internal: true, color: "btn-warning" }]
      : [] ),
    ...( [2, 15].includes(currentModuleId)
      ? [{ label: "Pending Verifications", icon: "bi-patch-check", href: "/pending-verifications", internal: true, color: "btn-danger" }]
      : [] ),
    ...( [2, 15].includes(currentModuleId)
      ? [{ label: "Employee List", icon: "bi-people", href: "/employee-list", internal: true, color: "btn-secondary" }]
      : [] ),
    ...( [2, 15].includes(currentModuleId)
      ? [{ label: "Sanctioned Posts", icon: "bi-building-check", href: "/sanctioned-posts", internal: true, color: "btn-indigo" }]
      : [] ),
    ...( [5, 13, 15, 17, 19, 21].includes(currentModuleId)
      ? [{ label: "Guest House", icon: "bi-building", href: "/guesthouse/booking-status", internal: true, color: "btn-secondary" }]
      : [] ),
    ...( [2, 5, 15].includes(currentModuleId)
      ? [{ label: "PendingComplaints", icon: "bi-hourglass-split", href: "/resolver-pending",  internal: true, color: "btn-warning" }]
      : [] ),
    ...( [2, 5, 15].includes(currentModuleId)
      ? [{ label: "Print Complaints",  icon: "bi-printer",         href: "/print-complaints", internal: true, color: "btn-info" }]
      : [] ),
    ...( currentModuleId === 2 && CONTRACTUAL_ATTENDANCE_DEPTS.includes(currentDeptId)
      ? [
          { label: "Attendance",    icon: "bi-calendar-check", href: "/attendance",            internal: true, color: "btn-primary" },
          { label: "Pending Leave", icon: "bi-hourglass-split", href: "/pending-leave-office",  internal: true, color: "btn-warning" }
        ]
      : [] )
  ];

  const renderLabelWithBadge = (item) => (
    <>
      {item.label}
      {Number(item.badgeCount) > 0 && (
        <span className="badge bg-danger ms-1">{item.badgeCount}</span>
      )}
    </>
  );

  return (
    <nav className={navClassName}>
      <div className="container-fluid d-flex align-items-center">

        {/* CENTER: Logo + Title */}
        <Link
          to={homeTarget}
          className={`navbar-brand d-flex align-items-center me-auto ${headerTextClass}`}
        >
          <img
            src="/icons/PULogo.png"
            alt="Logo"
            style={{ height: "44px", marginRight: "8px" }}
          />
          SARVATRA
        </Link>

        {/* RIGHT: Header actions */}
        <div className="d-flex align-items-center">
          <Link to={homeTarget} className={`${headerTextClass} me-2 top-header-link-icon`}>
            <i className="bi bi-house"></i>
          </Link>

          {isGuesthouseAdmin && (
            <button
              type="button"
              onClick={toggleGhNotify}
              className={`btn btn-link p-0 me-2 top-header-link-icon ${ghNotifyEnabled ? headerTextClass : "text-warning"}`}
              title={ghNotifyEnabled ? "Guest email/SMS notifications: ON (click to turn off)" : "Guest email/SMS notifications: OFF (click to turn on)"}
              aria-label="Toggle guest booking email/SMS notifications"
              aria-pressed={ghNotifyEnabled}
            >
              <i className={`bi ${ghNotifyEnabled ? "bi-bell-fill" : "bi-bell-slash-fill"}`}></i>
            </button>
          )}

          {Boolean(localUser) && (
            <div className="d-flex align-items-center me-2" style={{ gap: "3px" }}>
              {filteredCommonMenuItems.map((item) => (
                item.action === "logout" ? (
                  <button
                    key={item.label}
                    type="button"
                    className="btn btn-light btn-sm employee-top-menu-btn header-icon-btn"
                    onClick={handleLogout}
                    title={item.label}
                    aria-label={item.label}
                  >
                    <i className={`bi ${item.icon}`}></i>
                  </button>
                ) : item.internal ? (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="btn btn-light btn-sm employee-top-menu-btn header-icon-btn"
                    title={item.label}
                    aria-label={item.label}
                  >
                    <i className={`bi ${item.icon}`}></i>
                  </Link>
                ) : (
                  <a
                    key={item.label}
                    href={item.href}
                    className="btn btn-light btn-sm employee-top-menu-btn header-icon-btn"
                    title={item.label}
                    aria-label={item.label}
                  >
                    <i className={`bi ${item.icon}`}></i>
                  </a>
                )
              ))}
            </div>
          )}

          {isLoggedIn ? (
            <span className={`${headerTextClass} me-3 user-icon-tooltip`}>
              <i className="bi bi-person-circle"></i>
              <span className="user-icon-tooltip-bubble">
                <span className="user-icon-tooltip-name">{localUser?.name || user?.username || "User"}</span>
                {localUser?.moduleName && (
                  <span className="user-icon-tooltip-module">{localUser.moduleName}</span>
                )}
              </span>
            </span>
          ) : (
            <Link to="/login" className={`${headerTextClass} top-header-link-icon`}>
              <i className="bi bi-box-arrow-in-right"></i>
            </Link>
          )}
        </div>
      </div>





      {showEmployeeTopMenu && (
        <div className={`employee-top-menu border-top w-100${menuOpen ? " menu-open" : ""}`}>
          {moduleMenuItems.length > 0 && (
            <>
              {/* Mobile-only toggle bar — hidden on desktop via CSS */}
              <button
                type="button"
                className="menu-toggle-btn"
                onClick={() => setMenuOpen(o => !o)}
                aria-expanded={menuOpen}
              >
                <i className={`bi ${menuOpen ? "bi-chevron-up" : "bi-list"} me-2`}></i>
                {menuOpen ? "Hide Menu" : "Menu"}
                <i className={`bi bi-chevron-${menuOpen ? "up" : "down"} ms-auto`}></i>
              </button>

              <div className="employee-top-menu-strip">
                {moduleMenuItems.map((item) => (
                  item.action === "logout" ? (
                    <button
                      key={item.label}
                      type="button"
                      className={`btn btn-sm employee-top-menu-btn ${item.color || 'btn-light'}`}
                      onClick={handleLogout}
                    >
                      <i className={`bi ${item.icon} me-1`}></i>
                      {renderLabelWithBadge(item)}
                    </button>
                  ) : item.internal ? (
                    <Link key={item.label} to={item.href} className={`btn btn-sm employee-top-menu-btn ${item.color || 'btn-light'}`}>
                      <i className={`bi ${item.icon} me-1`}></i>
                      {renderLabelWithBadge(item)}
                    </Link>
                  ) : (
                    <a
                      key={item.label}
                      href={item.href}
                      className={`btn btn-sm employee-top-menu-btn ${item.color || 'btn-light'}`}
                    >
                      <i className={`bi ${item.icon} me-1`}></i>
                      {renderLabelWithBadge(item)}
                    </a>
                  )
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </nav>
  );
}